/**
 * MyPodOps Studio V2 - VideoSDK.live WebRTC Integration
 *
 * Handles:
 * - Meeting initialization and joining
 * - Participant management
 * - Audio/video stream handling
 * - Screen sharing
 * - Events and callbacks
 */

import { Participant } from './session';

// VideoSDK CDN URL - single source of truth
const VIDEO_SDK_URL = 'https://sdk.videosdk.live/js-sdk/0.0.86/videosdk.js';

// Global promise lock for single-flight SDK loading
let sdkLoadPromise: Promise<void> | null = null;

// VideoSDK types (extend as needed from SDK docs)
interface VideoSDKMeeting {
  join(): Promise<void>;
  leave(): Promise<void>;
  end(): Promise<void>;
  enableMic(): void;
  disableMic(): void;
  enableWebcam(): void;
  disableWebcam(): void;
  enableScreenShare(): void;
  disableScreenShare(): void;
  startRecording(config?: RecordingConfig): void;
  stopRecording(): void;
  startLivestream(config: LivestreamConfig): void;
  stopLivestream(): void;
  sendChatMessage(message: string): void;
  participants: Map<string, VideoSDKParticipant>;
  localParticipant: VideoSDKParticipant;
  on(event: string, callback: (...args: any[]) => void): void;
  off(event: string, callback: (...args: any[]) => void): void;
}

interface VideoSDKParticipant {
  id: string;
  displayName: string;
  isLocal: boolean;
  micOn: boolean;
  webcamOn: boolean;
  screenShareOn: boolean;
  streams: Map<string, VideoSDKStream>;
  on(event: string, callback: (...args: any[]) => void): void;
  off(event: string, callback: (...args: any[]) => void): void;
}

interface VideoSDKStream {
  id: string;
  kind: 'audio' | 'video' | 'share';
  track: MediaStreamTrack;
  consumer?: any;
}

interface RecordingConfig {
  webhookUrl?: string;
  awsDirPath?: string;
  layout?: {
    type: string;
    priority: string;
    gridSize: number;
  };
}

// Livestream output configuration (for RTMP destinations)
export interface LivestreamOutput {
  url: string;       // RTMP URL (e.g., rtmp://a.rtmp.youtube.com/live2)
  streamKey: string; // Stream key for the platform
}

// Livestream configuration for VideoSDK native RTMP streaming
export interface LivestreamConfig {
  outputs: LivestreamOutput[];
  layout?: {
    type: 'GRID' | 'SPOTLIGHT' | 'SIDEBAR';
    priority?: 'SPEAKER' | 'PIN';
    gridSize?: number;
  };
  theme?: 'DEFAULT' | 'LIGHT' | 'DARK';
  quality?: 'low' | 'med' | 'high';
  orientation?: 'landscape' | 'portrait';
}

export interface VideoSDKConfig {
  meetingId: string;
  token: string;
  participantId: string;
  name: string;
  micEnabled?: boolean;
  webcamEnabled?: boolean;
  maxResolution?: '4k' | '1080p' | '720p' | '480p';
}

export interface VideoSDKEvents {
  onMeetingJoined?: () => void;
  onMeetingLeft?: () => void;
  onParticipantJoined?: (participant: VideoSDKParticipant) => void;
  onParticipantLeft?: (participantId: string) => void;
  onSpeakerChanged?: (participantId: string | null) => void;
  onMicRequested?: (data: { accept: () => void; reject: () => void }) => void;
  onWebcamRequested?: (data: { accept: () => void; reject: () => void }) => void;
  onChatMessage?: (message: { senderId: string; senderName: string; message: string; timestamp: number }) => void;
  onRecordingStarted?: () => void;
  onRecordingStopped?: (url?: string) => void;
  onLivestreamStarted?: () => void;
  onLivestreamStopped?: () => void;
  onError?: (error: Error) => void;
}

declare global {
  interface Window {
    VideoSDK: {
      config(token: string): void;
      initMeeting(config: {
        meetingId: string;
        participantId: string;
        name: string;
        micEnabled: boolean;
        webcamEnabled: boolean;
        maxResolution: string;
      }): VideoSDKMeeting;
    };
  }
}

/**
 * VideoSDKManager - Manages VideoSDK.live WebRTC meetings
 */
export class VideoSDKManager {
  private meeting: VideoSDKMeeting | null = null;
  private config: VideoSDKConfig | null = null;
  private events: VideoSDKEvents = {};
  private localStream: MediaStream | null = null;
  private participantStreams: Map<string, MediaStream> = new Map();

  constructor() {
    console.log('[VideoSDK] Manager initialized');
  }

  /**
   * Check if VideoSDK script is loaded
   */
  isSDKLoaded(): boolean {
    return typeof window !== 'undefined' && window.VideoSDK !== undefined;
  }

  /**
   * Load VideoSDK script - SINGLE-FLIGHT pattern
   * Uses global promise lock to prevent race conditions.
   * Only ONE script load ever happens, no timeouts, no fallbacks.
   */
  async loadSDK(): Promise<void> {
    // Return existing promise if already loading (prevents race condition)
    if (sdkLoadPromise) {
      console.log('[VideoSDK] SDK load already in progress, waiting...');
      return sdkLoadPromise;
    }

    // Already loaded and ready - resolve immediately
    if (this.isSDKLoaded()) {
      console.log('[VideoSDK] SDK already loaded');
      return Promise.resolve();
    }

    // Create single-flight promise with global lock
    sdkLoadPromise = new Promise<void>((resolve, reject) => {
      // Double-check in case it loaded between checks
      if (this.isSDKLoaded()) {
        console.log('[VideoSDK] SDK became available');
        resolve();
        return;
      }

      // Check if script already exists in DOM (from any source)
      const existingScript = document.querySelector(`script[src*="sdk.videosdk.live"]`) as HTMLScriptElement | null;

      if (existingScript) {
        console.log('[VideoSDK] Script element found, attaching load handlers...');

        // Script exists - attach handlers to it
        const handleLoad = () => {
          if (this.isSDKLoaded()) {
            console.log('[VideoSDK] SDK loaded successfully (existing script)');
            resolve();
          } else {
            const errorMsg = 'VideoSDK script loaded but window.VideoSDK is undefined';
            console.error('[VideoSDK]', errorMsg, {
              src: existingScript.src,
              online: navigator.onLine,
              userAgent: navigator.userAgent.substring(0, 100)
            });
            reject(new Error(errorMsg));
          }
          cleanup();
        };

        const handleError = () => {
          const errorMsg = 'Failed to load VideoSDK from existing script';
          console.error('[VideoSDK]', errorMsg, {
            src: existingScript.src,
            online: navigator.onLine,
            userAgent: navigator.userAgent.substring(0, 100)
          });
          reject(new Error(errorMsg));
          cleanup();
        };

        const cleanup = () => {
          existingScript.removeEventListener('load', handleLoad);
          existingScript.removeEventListener('error', handleError);
        };

        // If script is already complete, check immediately
        if (existingScript.dataset.loaded === 'true' || this.isSDKLoaded()) {
          handleLoad();
          return;
        }

        existingScript.addEventListener('load', handleLoad);
        existingScript.addEventListener('error', handleError);
        return;
      }

      // No script exists - create fresh one
      console.log('[VideoSDK] Creating new script element...');
      const script = document.createElement('script');
      script.src = VIDEO_SDK_URL;
      script.async = true;

      script.onload = () => {
        script.dataset.loaded = 'true';
        if (this.isSDKLoaded()) {
          console.log('[VideoSDK] SDK loaded successfully (new script)');
          resolve();
        } else {
          const errorMsg = 'VideoSDK script loaded but window.VideoSDK is undefined';
          console.error('[VideoSDK]', errorMsg, {
            src: script.src,
            online: navigator.onLine,
            userAgent: navigator.userAgent.substring(0, 100)
          });
          reject(new Error(errorMsg));
        }
      };

      script.onerror = () => {
        const errorMsg = 'Failed to load VideoSDK script';
        console.error('[VideoSDK]', errorMsg, {
          src: script.src,
          online: navigator.onLine,
          userAgent: navigator.userAgent.substring(0, 100)
        });
        reject(new Error(errorMsg));
      };

      document.head.appendChild(script);
    });

    return sdkLoadPromise;
  }

  /**
   * Initialize and join a meeting
   */
  async joinMeeting(config: VideoSDKConfig, events: VideoSDKEvents = {}): Promise<void> {
    this.config = config;
    this.events = events;

    // Ensure SDK is loaded
    await this.loadSDK();

    if (!window.VideoSDK) {
      throw new Error('VideoSDK not available');
    }

    console.log(`[VideoSDK] Joining meeting: ${config.meetingId}`);

    // Configure token first (required by VideoSDK)
    window.VideoSDK.config(config.token);

    // Initialize the meeting (without token in config)
    this.meeting = window.VideoSDK.initMeeting({
      meetingId: config.meetingId,
      participantId: config.participantId,
      name: config.name,
      micEnabled: config.micEnabled ?? true,
      webcamEnabled: config.webcamEnabled ?? true,
      maxResolution: config.maxResolution || '1080p'
    });

    // Set up event listeners
    this.setupEventListeners();

    // Join the meeting
    await this.meeting.join();
  }

  /**
   * Set up VideoSDK event listeners
   */
  private setupEventListeners(): void {
    if (!this.meeting) return;

    // Meeting joined
    this.meeting.on('meeting-joined', () => {
      console.log('[VideoSDK] Meeting joined');
      this.handleLocalParticipant();
      this.events.onMeetingJoined?.();
    });

    // Meeting left
    this.meeting.on('meeting-left', () => {
      console.log('[VideoSDK] Meeting left');
      this.cleanup();
      this.events.onMeetingLeft?.();
    });

    // Participant joined
    this.meeting.on('participant-joined', (participant: VideoSDKParticipant) => {
      console.log(`[VideoSDK] Participant joined: ${participant.displayName}`);
      this.handleParticipantJoined(participant);
    });

    // Participant left
    this.meeting.on('participant-left', (participant: VideoSDKParticipant) => {
      console.log(`[VideoSDK] Participant left: ${participant.displayName}`);
      this.participantStreams.delete(participant.id);
      this.events.onParticipantLeft?.(participant.id);
    });

    // Active speaker changed
    this.meeting.on('speaker-changed', (participantId: string | null) => {
      this.events.onSpeakerChanged?.(participantId);
    });

    // Mic requested
    this.meeting.on('mic-requested', (data: { accept: () => void; reject: () => void }) => {
      this.events.onMicRequested?.(data);
    });

    // Webcam requested
    this.meeting.on('webcam-requested', (data: { accept: () => void; reject: () => void }) => {
      this.events.onWebcamRequested?.(data);
    });

    // Chat message
    this.meeting.on('chat-message', (data: { senderId: string; senderName: string; text: string; timestamp: Date }) => {
      this.events.onChatMessage?.({
        senderId: data.senderId,
        senderName: data.senderName,
        message: data.text,
        timestamp: data.timestamp.getTime()
      });
    });

    // Recording started
    this.meeting.on('recording-started', () => {
      console.log('[VideoSDK] Recording started');
      this.events.onRecordingStarted?.();
    });

    // Recording stopped
    this.meeting.on('recording-stopped', (url?: string) => {
      console.log('[VideoSDK] Recording stopped:', url);
      this.events.onRecordingStopped?.(url);
    });

    // Livestream started
    this.meeting.on('livestream-started', () => {
      console.log('[VideoSDK] Livestream started');
      this.events.onLivestreamStarted?.();
    });

    // Livestream stopped
    this.meeting.on('livestream-stopped', () => {
      console.log('[VideoSDK] Livestream stopped');
      this.events.onLivestreamStopped?.();
    });

    // Error
    this.meeting.on('error', (error: Error) => {
      console.error('[VideoSDK] Error:', error);
      this.events.onError?.(error);
    });
  }

  /**
   * Handle local participant setup
   */
  private handleLocalParticipant(): void {
    if (!this.meeting) return;

    const local = this.meeting.localParticipant;
    if (!local) return;

    // Listen for stream events on local participant
    local.on('stream-enabled', (stream: VideoSDKStream) => {
      console.log(`[VideoSDK] Local stream enabled: ${stream.kind}`);
      this.updateLocalStream();
    });

    local.on('stream-disabled', (stream: VideoSDKStream) => {
      console.log(`[VideoSDK] Local stream disabled: ${stream.kind}`);
      this.updateLocalStream();
    });

    this.updateLocalStream();
  }

  /**
   * Update local MediaStream from VideoSDK streams
   */
  private updateLocalStream(): void {
    if (!this.meeting) return;

    const local = this.meeting.localParticipant;
    if (!local) return;

    this.localStream = new MediaStream();

    local.streams.forEach((stream: VideoSDKStream) => {
      if (stream.track) {
        this.localStream!.addTrack(stream.track);
      }
    });
  }

  /**
   * Handle when a participant joins
   */
  private handleParticipantJoined(participant: VideoSDKParticipant): void {
    // Set up stream listeners
    participant.on('stream-enabled', (stream: VideoSDKStream) => {
      console.log(`[VideoSDK] Participant ${participant.id} stream enabled: ${stream.kind}`);
      this.updateParticipantStream(participant);
    });

    participant.on('stream-disabled', (stream: VideoSDKStream) => {
      console.log(`[VideoSDK] Participant ${participant.id} stream disabled: ${stream.kind}`);
      this.updateParticipantStream(participant);
    });

    this.updateParticipantStream(participant);
    this.events.onParticipantJoined?.(participant);
  }

  /**
   * Update participant MediaStream
   */
  private updateParticipantStream(participant: VideoSDKParticipant): void {
    const stream = new MediaStream();

    participant.streams.forEach((s: VideoSDKStream) => {
      if (s.track) {
        stream.addTrack(s.track);
      }
    });

    this.participantStreams.set(participant.id, stream);
  }

  /**
   * Leave the meeting
   */
  async leaveMeeting(): Promise<void> {
    if (!this.meeting) return;

    console.log('[VideoSDK] Leaving meeting');
    await this.meeting.leave();
    this.cleanup();
  }

  /**
   * End the meeting (host only)
   */
  async endMeeting(): Promise<void> {
    if (!this.meeting) return;

    console.log('[VideoSDK] Ending meeting');
    await this.meeting.end();
    this.cleanup();
  }

  /**
   * Toggle microphone
   */
  toggleMic(): boolean {
    if (!this.meeting) return false;

    const local = this.meeting.localParticipant;
    if (!local) return false;

    if (local.micOn) {
      this.meeting.disableMic();
      return false;
    } else {
      this.meeting.enableMic();
      return true;
    }
  }

  /**
   * Toggle webcam
   */
  toggleWebcam(): boolean {
    if (!this.meeting) return false;

    const local = this.meeting.localParticipant;
    if (!local) return false;

    if (local.webcamOn) {
      this.meeting.disableWebcam();
      return false;
    } else {
      this.meeting.enableWebcam();
      return true;
    }
  }

  /**
   * Toggle screen share
   */
  toggleScreenShare(): boolean {
    if (!this.meeting) return false;

    const local = this.meeting.localParticipant;
    if (!local) return false;

    if (local.screenShareOn) {
      this.meeting.disableScreenShare();
      return false;
    } else {
      this.meeting.enableScreenShare();
      return true;
    }
  }

  /**
   * Send a chat message
   */
  sendMessage(message: string): void {
    if (!this.meeting) return;
    this.meeting.sendChatMessage(message);
  }

  /**
   * Start cloud recording (VideoSDK feature)
   */
  startCloudRecording(webhookUrl?: string): void {
    if (!this.meeting) return;

    this.meeting.startRecording({
      webhookUrl,
      layout: {
        type: 'GRID',
        priority: 'SPEAKER',
        gridSize: 4
      }
    });
  }

  /**
   * Stop cloud recording
   */
  stopCloudRecording(): void {
    if (!this.meeting) return;
    this.meeting.stopRecording();
  }

  /**
   * Start livestream to RTMP destinations (VideoSDK Native Streaming)
   * @param outputs Array of RTMP destinations with url and streamKey
   */
  startLivestream(outputs: LivestreamOutput[]): void {
    if (!this.meeting) {
      console.warn('[VideoSDK] Cannot start livestream - no active meeting');
      return;
    }

    if (!outputs || outputs.length === 0) {
      console.warn('[VideoSDK] Cannot start livestream - no outputs provided');
      return;
    }

    console.log('[VideoSDK] Starting livestream to', outputs.length, 'destination(s)');
    this.meeting.startLivestream({ outputs });
  }

  /**
   * Stop livestream
   */
  stopLivestream(): void {
    if (!this.meeting) {
      console.warn('[VideoSDK] Cannot stop livestream - no active meeting');
      return;
    }

    console.log('[VideoSDK] Stopping livestream');
    this.meeting.stopLivestream();
  }

  /**
   * Get local MediaStream
   */
  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Get participant MediaStream
   */
  getParticipantStream(participantId: string): MediaStream | null {
    return this.participantStreams.get(participantId) || null;
  }

  /**
   * Get all participant streams
   */
  getAllParticipantStreams(): Map<string, MediaStream> {
    return this.participantStreams;
  }

  /**
   * Get all participants (including local)
   */
  getParticipants(): VideoSDKParticipant[] {
    if (!this.meeting) return [];

    const participants: VideoSDKParticipant[] = [];

    if (this.meeting.localParticipant) {
      participants.push(this.meeting.localParticipant);
    }

    this.meeting.participants.forEach((p) => {
      participants.push(p);
    });

    return participants;
  }

  /**
   * Convert VideoSDK participant to our Participant format
   */
  toParticipant(p: VideoSDKParticipant): Participant {
    const stream = p.isLocal ? this.localStream : this.participantStreams.get(p.id);

    return {
      id: p.id,
      name: p.displayName,
      role: p.isLocal ? 'host' : 'guest',
      status: 'joined',
      tracks: {
        video: stream?.getVideoTracks()[0],
        audio: stream?.getAudioTracks()[0]
      },
      isLocal: p.isLocal,
      isMuted: !p.micOn,
      isVideoOff: !p.webcamOn,
      joinedAt: Date.now()
    };
  }

  /**
   * Check if meeting is active
   */
  isActive(): boolean {
    return this.meeting !== null;
  }

  /**
   * Get meeting ID
   */
  getMeetingId(): string | null {
    return this.config?.meetingId || null;
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.meeting = null;
    this.localStream = null;
    this.participantStreams.clear();
    console.log('[VideoSDK] Cleanup complete');
  }
}

// Export singleton instance
export const videoSDK = new VideoSDKManager();

// Export default
export default videoSDK;
